import { lazy } from "react";
import PrivateRoute from "./components/shared/PrivateRoute";
import Home from "./components/pages/common/Home";
import DashboardLayout from "./components/shared/layouts/DashboardLayout";
import MainLayout from "./components/shared/layouts/MainLayout";
import { ROUTES_URL } from "./utils/constants";

const Login = lazy(() => import("./components/pages/account/Login"));
const Signup = lazy(() => import("./components/pages/account/Signup"));
const Announcement = lazy(() => import("./components/pages/app/Announcement/Announcement"));
const AnnouncementDetail = lazy(() => import("./components/pages/app/Announcement/AnnouncementDetail"));
const Dashboard = lazy(() => import("./components/pages/app/Dashboard/Dashboard"));
const StudioDetail = lazy(() => import("./components/pages/app/Studios/StudioDetail"));
const Studios = lazy(() => import("./components/pages/app/Studios/Studios"));
const TrackDetail = lazy(() => import("./components/pages/app/Tracks/TrackDetail"));
const About = lazy(() => import("./components/pages/common/About"));
const Contact = lazy(() => import("./components/pages/common/Contact"));
const Profile = lazy(() => import("./components/pages/account/Profile/Profile"));
const News = lazy(() => import("./components/pages/app/News/News"));
const NewsDetail = lazy(() => import("./components/pages/app/News/NewsDetail"));
const Events = lazy(() => import("./components/pages/app/Events/Events"));
const EventDetail = lazy(() => import("./components/pages/app/Events/EventDetail"));
const VerifyEmail = lazy(() => import("./components/pages/account/VerifyEmail"));
const ForgotPassword = lazy(() => import("./components/pages/account/ForgotPassword"));
const ResetPassword = lazy(() => import("./components/pages/account/ResetPassword"));
const AnnouncementAdd = lazy(() => import("./components/pages/app/Announcement/AnnouncementAdd"));
const TrackAdd = lazy(() => import("./components/pages/app/Tracks/TrackAdd"));
const NewsAdd = lazy(() => import("./components/pages/app/News/NewsAdd"));
const User = lazy(() => import("./components/pages/app/Users/User"));
const Messages = lazy(() => import("./components/pages/app/Users/Messages"));
const TrackList = lazy(() => import("./components/pages/app/Tracks/TrackList"));
const PrivacyPolicy = lazy(() => import("./components/pages/common/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./components/pages/common/TermsOfUse"));
const Products = lazy(() => import("./components/pages/app/Products/Products"));
const ProductAdd = lazy(() => import("./components/pages/app/Products/ProductAdd"));
const ProductDetail = lazy(() => import("./components/pages/app/Products/ProductDetail"));
const UserList = lazy(() => import("./components/pages/app/Users/UserList"));
const Questions = lazy(() => import("./components/pages/app/QA/Questions"));
const QuestionDetail = lazy(() => import("./components/pages/app/QA/QuestionDetail"));
const PublicChat = lazy(() => import("./components/pages/app/PublicChat/PublicChat"));
const Ranks = lazy(() => import("./components/pages/app/Ranks/Ranks"));
const PublicMusicianCard = lazy(() => import("./components/pages/public/PublicMusicianCard"));
const NotFound = lazy(() => import("./components/pages/common/NotFound"));

/** @type {import('react-router-dom/dist').RouteObject[]} */
const Routes = [
  {
    path: "/",
    element: <MainLayout />,
    children: [
      {
        index: true,
        element: <Home />,
      },

      {
        path: ROUTES_URL.FORGOT_PASSWORD,
        element: <ForgotPassword />,
      },
      {
        path: ROUTES_URL.RESET_PASSWORD,
        element: <ResetPassword />,
      },
      {
        path: ROUTES_URL.LOGIN,
        element: <Login />,
      },
      {
        path: ROUTES_URL.VERIFY_EMAIL,
        element: <VerifyEmail />,
      },
      {
        path: ROUTES_URL.SIGNUP,
        element: <Signup />,
      },
      {
        path: ROUTES_URL.ABOUT,
        element: <About />,
      },
      {
        path: ROUTES_URL.CONTACT,
        element: <Contact />,
      },
      {
        path: ROUTES_URL.PRIVACY_POLICY,
        element: <PrivacyPolicy />,
      },
      {
        path: ROUTES_URL.TERMS_OF_USE,
        element: <TermsOfUse />,
      },
      {
        path: ":username",
        element: <PublicMusicianCard />,
      },
      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
  {
    path: ROUTES_URL.DASHBOARD,
    element: <DashboardLayout />,
    children: [
      {
        private: false,
        path: "",
        element: <Dashboard />,
      },
      {
        private: true,
        path: ROUTES_URL.TRACKS,
        element: <TrackList />,
      },
      {
        path: ROUTES_URL.ANNOUNCEMENTS,
        element: <Announcement />,
        private: false,
      },
      {
        path: ROUTES_URL.ANNOUNCEMENT_ADD,
        element: <AnnouncementAdd />,
        private: true,
      },
      {
        path: ROUTES_URL.STUDIOS,
        element: <Studios />,
        private: false,
      },
      {
        path: ROUTES_URL.STUDIO_DETAIL,
        element: <StudioDetail />,
        private: false,
      },
      {
        path: ROUTES_URL.ANNOUNCEMENT_DETAIL,
        element: <AnnouncementDetail />,
        private: false,
      },
      {
        path: ROUTES_URL.TRACK_DETAIL,
        element: <TrackDetail />,
        private: true,
      },
      {
        path: ROUTES_URL.TRACK_ADD,
        element: <TrackAdd />,
        private: true,
      },
      {
        path: ROUTES_URL.NEWS,
        element: <News />,
        private: false,
      },
      {
        path: ROUTES_URL.NEWS_ADD,
        element: <NewsAdd />,
        private: true,
      },
      {
        path: ROUTES_URL.NEWS_DETAIL,
        element: <NewsDetail />,
        private: false,
      },
      {
        path: ROUTES_URL.PROFILE,
        element: <Profile />,
        private: true,
      },
      {
        path: ROUTES_URL.USER_DETAIL,
        element: <User />,
        private: true,
      },
      {
        path: ROUTES_URL.USER_LIST,
        element: <UserList />,
        private: true,
      },
      {
        path: ROUTES_URL.USER_MESSAGES,
        element: <Messages />,
        private: true,
      },
      {
        path: ROUTES_URL.EVENTS,
        element: <Events />,
        private: false,
      },
      {
        path: ROUTES_URL.EVENT_DETAIL,
        element: <EventDetail />,
        private: false,
      },
      {
        path: ROUTES_URL.PRODUCTS,
        element: <Products />,
        private: false,
      },
      {
        path: ROUTES_URL.PRODUCT_ADD,
        element: <ProductAdd />,
        private: true,
      },
      {
        path: ROUTES_URL.PRODUCT_DETAIL,
        element: <ProductDetail />,
        private: false,
      },
      {
        path: ROUTES_URL.PUBLIC_CHAT,
        element: <PublicChat />,
        private: true,
      },
      {
        path: ROUTES_URL.RANKS_PAGE,
        element: <Ranks />,
        private: false,
      },
      {
        path: ROUTES_URL.QA,
        element: <Questions />,
        private: false,
      },
      {
        path: ROUTES_URL.QA_DETAIL,
        element: <QuestionDetail />,
        private: false,
      },
      {
        path: "*",
        element: <NotFound />,
        private: false,
      },
    ],
  },
];

const prepareRoutes = (routes) => {
  return routes.map((route) => {
    if (route?.children) {
      return {
        ...route,
        children: prepareRoutes(route.children),
      };
    }

    if (route?.private) {
      return {
        ...route,
        element: <PrivateRoute>{route.element}</PrivateRoute>,
      };
    }
    // <HistoryRouter history={history}></HistoryRouter>
    return route;
  });
};

const finalRoutes = prepareRoutes(Routes);

export default finalRoutes;
